import { DOCUMENT } from '@angular/common';
import { Injectable, RendererFactory2, signal, effect, computed, inject, isDevMode } from '@angular/core';
import { THEME_PREF_KEY } from '../constants';

// The user's explicit choice. 'system' means we follow the OS.
export type ThemePreference = 'light' | 'dark' | 'system';

// The theme that is actually applied to the app.
type ActiveTheme = 'light' | 'dark';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private renderer = inject(RendererFactory2).createRenderer(null, null);
  private document = inject(DOCUMENT);

  private preference = signal<ThemePreference>(this.getInitialPreference());
  private systemTheme = signal<ActiveTheme>('light');

  /**
   * A computed signal that determines the active theme.
   */
  private activeTheme = computed<ActiveTheme>(() => {
    const pref = this.preference();
    if (pref === 'system') {
      return this.systemTheme();
    }
    // If we are here, we know `pref` is 'light' or 'dark', which matches ActiveTheme.
    return pref;
  });

  // Public readonly signals for components to consume.]
  // The 'computed' signal is already read-only from an external perspective.
  public readonly theme = this.activeTheme;
  public readonly currentPreference = this.preference;

  constructor() {
    this.initializeSystemListener();

    effect(() => {
      this.updateBodyClass(this.activeTheme());
      if (isDevMode()) {
        console.log(`Theme preference: ${this.preference()}, Active theme: ${this.activeTheme()}`);
      }
    });
  }

  public setPreference(preference: ThemePreference): void {
    this.preference.set(preference);
    localStorage.setItem(THEME_PREF_KEY, preference);
  }

  private initializeSystemListener(): void {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    this.systemTheme.set(mediaQuery.matches ? 'dark' : 'light');

    mediaQuery.addEventListener('change', (event) => {
      this.systemTheme.set(event.matches ? 'dark' : 'light');
    });
  }

  private getInitialPreference(): ThemePreference {
    if (typeof localStorage === 'undefined') return 'system';

    const storedPreference = localStorage.getItem(THEME_PREF_KEY) as ThemePreference;
    return storedPreference || 'system';
  }

  private updateBodyClass(theme: ActiveTheme): void {
    if (theme === 'dark') {
      this.renderer.addClass(this.document.body, 'dark-theme');
    } else {
      this.renderer.removeClass(this.document.body, 'dark-theme');
    }
  }
}